import React from 'react';
import { translations, stateDistrictData } from './data';

const PrintSummary = ({ formData, language, onNewRegistration }) => {
  const t = translations[language];
  
  // Get display values for state and district
  const getStateDisplay = (state) => {
    return language === 'hi' ? stateDistrictData[state]?.hi || state : state;
  };
  
  const getDistrictDisplay = (state, district) => {
    const stateData = stateDistrictData[state];
    if (!stateData) return district;
    
    const districtIndex = stateData.districts.en.indexOf(district);
    if (districtIndex === -1) return district;
    
    return language === 'hi' ? stateData.districts.hi[districtIndex] : district;
  };
  
  const getGenderDisplay = (gender) => {
    switch(gender) {
      case 'male': return t.male;
      case 'female': return t.female;
      case 'other': return t.other;
      default: return gender;
    }
  };
  
  const getOccupationDisplay = (occupation) => {
    switch(occupation) {
      case 'business': return t.business;
      case 'privateJob': return t.privateJob;
      case 'governmentJob': return t.governmentJob;
      case 'selfEmployed': return t.selfEmployed;
      default: return occupation;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-indigo-50 print:bg-white">
      <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Success Message */}
          <div className="bg-white rounded-2xl shadow-xl p-8 print:shadow-none print:rounded-none">
            <div className="text-center mb-8 print:hidden">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
                {t.registrationSuccess}
              </h1>
              <p className="text-gray-600">
                {language === 'en' ? 'Your registration has been saved successfully!' : 'आपका पंजीकरण सफलतापूर्वक सहेज लिया गया है!'}
              </p>
            </div>
          
            {/* Registration Details */}
            <div className="bg-gray-50 rounded-xl p-6 border print:bg-white print:border-gray-400">
              <h1 className="text-2xl sm:text-3xl font-bold text-center mb-6 text-gray-800 border-b border-gray-300 pb-3 print:text-xl">
                {t.registrationDetails}
              </h1>
              
              <div className="grid gap-4">
                <div className="flex flex-col sm:flex-row sm:justify-between py-3 border-b border-gray-200">
                  <span className="font-semibold text-gray-700 mb-1 sm:mb-0">{t.name}:</span>
                  <span className="text-gray-900 font-medium">{formData.name}</span>
                </div>
                
                <div className="flex flex-col sm:flex-row sm:justify-between py-3 border-b border-gray-200">
                  <span className="font-semibold text-gray-700 mb-1 sm:mb-0">{t.mobileNumber}:</span>
                  <span className="text-gray-900 font-medium">{formData.mobileNumber}</span>
                </div>
                
                <div className="flex flex-col sm:flex-row sm:justify-between py-3 border-b border-gray-200">
                  <span className="font-semibold text-gray-700 mb-1 sm:mb-0">{t.whatsappQuestion}:</span>
                  <span className={`font-medium ${formData.isWhatsApp ? 'text-green-600' : 'text-red-600'}`}>
                    {formData.isWhatsApp ? t.yes : t.no}
                  </span>
                </div>
                
                <div className="flex flex-col sm:flex-row sm:justify-between py-3 border-b border-gray-200">
                  <span className="font-semibold text-gray-700 mb-1 sm:mb-0">{t.gender}:</span>
                  <span className="text-gray-900 font-medium">{getGenderDisplay(formData.gender)}</span>
                </div>
                
                <div className="flex flex-col sm:flex-row sm:justify-between py-3 border-b border-gray-200">
                  <span className="font-semibold text-gray-700 mb-1 sm:mb-0">{t.age}:</span>
                  <span className="text-gray-900 font-medium">{formData.age}</span>
                </div>
                
                <div className="flex flex-col sm:flex-row sm:justify-between py-3 border-b border-gray-200">
                  <span className="font-semibold text-gray-700 mb-1 sm:mb-0">{t.occupation}:</span>
                  <span className="text-gray-900 font-medium">{getOccupationDisplay(formData.occupation)}</span>
                </div>
                
                <div className="flex flex-col sm:flex-row sm:justify-between py-3 border-b border-gray-200">
                  <span className="font-semibold text-gray-700 mb-1 sm:mb-0">{t.state}:</span>
                  <span className="text-gray-900 font-medium">{getStateDisplay(formData.state)}</span>
                </div>
                
                <div className="flex flex-col sm:flex-row sm:justify-between py-3">
                  <span className="font-semibold text-gray-700 mb-1 sm:mb-0">{t.district}:</span>
                  <span className="text-gray-900 font-medium">{getDistrictDisplay(formData.state, formData.district)}</span>
                </div>
              </div>
              
              <div className="mt-6 pt-4 border-t border-gray-300 text-center">
                <p className="text-sm text-gray-500 font-medium">
                  {language === 'en' ? 'Registration Date:' : 'पंजीकरण दिनांक:'} {new Date().toLocaleString()}
                </p>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4 no-print">
              <button
                onClick={() => window.print()}
                className="bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                {t.print}
              </button>
              
              <button
                onClick={onNewRegistration}
                className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-3 rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {t.newRegistration}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintSummary;